const _ = require("lodash");

const AT = require("../../core/vault/ActionTypes");
const BookstoreUtil = require("../../../shared/BookstoreUtil");
const OpsUser = require("../../core/vault/OpsUser");
const {atCurrLibraryChapter} = require("../../core/vault/StateCursors");
const {setIn} = require("../../../shared/ImmHelpers");
const ChapterSection = require("../../../shared/ChapterSection");
const PageShared = require("../../../shared/PageShared");
const URLMake = require("../../../shared/URLMake");
const {loadSuccess} = require("../../core/vault/util/LoadState");

// Library Chapters shape {
//   [gradeNumber]: {
//     [chapterID]: {
//       model: {
//         chapterID,
//         isCompletelyRead: is finished reading chapter,
//         pagesRead: array of pages read in chapter,
//         sectionsMarkedDone: array of sectionIDs which were marked "Done!".
//         lastPageVisited: last page visited on chapter,
//         updateFails: integer with number of failed update calls, 0 iff last succeeded,
//         lastFailTime: a Date.now return with the last time the update call failed,
//       },
//     }
//     load,
//     loadError,
//   }
// }

const setupThrottlerInVault = function(vault) {
	const {api, throttler} = vault;

	throttler.config("libraryChapter", {
		delay: 10000,
		noLeading: true,
		getID: obj => _.get(obj, "libraryChapter.model.chapterID", 0),
		run: obj =>
			updateChapterData(
				obj.libraryChapter,
				obj.userID,
				obj.page,
				api,
				throttler
			),
	});
};

const initChapterModel = function(chapterID, response) {
	return {
		chapterID,
		isCompletelyRead: response.isCompletelyRead,
		pagesRead: response.pagesRead,
		lastPageVisited: response.lastPageVisited,
		updateFails: 0,
		lastFailTime: null,
		sectionsMarkedDone: response.sectionsMarkedDone || [],
	};
};

const initChapter = function(chapterID, response) {
	return loadSuccess({
		chapterID,
		model: initChapterModel(chapterID, response),
	});
};

const sectionIsRead = (libraryChapter, sectionID) => {
	const chapterModel = _.get(libraryChapter, "model");

	const pagesRead = _.get(chapterModel, "pagesRead");
	const pages = ChapterSection.getChapterSectionPages(sectionID);
	const [first, last] = pages;
	const sectionPages = _.range(first, last + 1);
	const sectionRead =
		_.intersection(sectionPages, pagesRead).length === sectionPages.length;

	const sectionsMarkedDone = _.get(chapterModel, "sectionsMarkedDone") || [];
	const markedDone = sectionsMarkedDone.includes(sectionID);
	return sectionRead || markedDone;
};

const updateChapterData = function(
	libraryChapter,
	userID,
	page,
	api,
	throttler
) {
	const chapterModel = _.get(libraryChapter, "model");
	if (chapterModel) {
		const chapterData = _.pick(chapterModel, [
			"isCompletelyRead",
			"pagesRead",
			"lastPageVisited",
			"sectionsMarkedDone",
		]);

		const pageNumber = chapterModel.lastPageVisited;
		const sectionID = ChapterSection.getSectionIDFromChapterPage(
			chapterModel.chapterID,
			pageNumber
		);
		let roomData;
		if (PageShared.isTrainerChapterPage(page)) {
			// we should only update room data if reading from the chapter page
			if (sectionID > 0) {
				roomData = {
					isReading: true,
					bookSectionID: sectionID,
					chapterID: chapterModel.chapterID,
					completed: sectionIsRead(libraryChapter, sectionID),
				};
			}
		}

		api.updateLibraryChapterData(
			chapterModel.chapterID,
			userID,
			chapterData,
			roomData
		);
		throttler.cancel("libraryChapter", {libraryChapter, userID, page});
	}
};

const getLastVisitedPageInChapter = function(chapterID, state) {
	const libraryChapter = atCurrLibraryChapter(state).get();
	return _.get(libraryChapter, ["model", "lastPageVisited"], 0);
};

const getLastVisitedPageFromNumbers = function(context, state) {
	const chapterID = ChapterSection.toChapterID(
		context.gradeNumber,
		context.chapterIndex
	);
	return getLastVisitedPageInChapter(chapterID, state);
};

const isValidLibraryGrade = function(page, state) {
	const gradeNumber = page.gradeNumber;
	if (page.demo && gradeNumber === 0) return true;
	return _.includes(
		BookstoreUtil.getAvailableLibraryGrades(),
		String(gradeNumber)
	);
};

// Take users to their last visited page in each chapter
// or to the last library grade level they last visited.
const fillLibraryPage = function(page, state) {
	const gradeNumber = page.gradeNumber;
	const chapterIndex = page.chapterIndex;
	if (!isValidLibraryGrade(page, state)) {
		const mostRecentPageData = OpsUser.getMostRecentPageData(state.user) || {};
		const userGradeNumber = _.get(
			mostRecentPageData,
			"gradeNumber",
			parseInt(BookstoreUtil.getAvailableLibraryGrades()[0])
		);
		return URLMake.state.forLibraryFromNumbers(userGradeNumber);
	}
	// Check if available chapter in grade
	// Default to library grade page
	const chapterNumber = chapterIndex + 1;
	// TODO: Edit this line when all grade 2 books become available
	const availableBooks = BookstoreUtil.getAvailableBooksInGrade(gradeNumber);
	const chapters = availableBooks.map(hash =>
		BookstoreUtil.getChapterNumbersByBookHash(hash)
	);
	const availableChapters = _.flatten(chapters);
	if (!chapterNumber || !_.includes(availableChapters, chapterNumber)) {
		return URLMake.state.forLibraryFromNumbers(gradeNumber);
	}
	// If last section is in the form p###, then go to page ### in that chapter.
	const pageObj = URLMake.state.forLibraryFromNumbers(
		gradeNumber,
		chapterIndex
	);
	const sectionIndex = page.sectionIndex;
	const sequencePage = _.get(page, "sequencePage");
	const chapterID = ChapterSection.toChapterID(gradeNumber, chapterIndex);
	const chapterPages = ChapterSection.getChapterPages(chapterID, page.demo);
	const chapterSections = ChapterSection.getChapterSections(chapterID);
	const lastPageVisited = getLastVisitedPageInChapter(chapterID, state);
	// Check if page or section is valid in chapter
	if (
		page.pageNumber &&
		_.inRange(page.pageNumber, chapterPages[0], chapterPages[1] + 1)
	) {
		pageObj.pageNumber = page.pageNumber;
	} else if (
		_.isInteger(sectionIndex) &&
		sectionIndex > -1 &&
		sectionIndex < chapterSections.length
	) {
		pageObj.sectionIndex = sectionIndex;
	} else {
		pageObj.pageNumber = lastPageVisited;
	}
	if (page.demo) pageObj.demo = true;
	// Check if sequence slideout page number reference is valid in chapter
	if (
		sequencePage &&
		_.inRange(sequencePage, chapterPages[0], chapterPages[1] + 1)
	) {
		pageObj.sequencePage = sequencePage;
	}

	return _.isEqual(page, pageObj) ? page : pageObj;
};

const updateReadPages = function(clock, chapterModel, pageJustRead, demo) {
	const chapterID = chapterModel.chapterID;
	const newPagesRead = _.clone(chapterModel.pagesRead);

	// Add the pageJustRead to the pagesRead list if not already there.
	// Insert new page so array stays sorted.
	// This check is already done in the view, maybe not needed here?
	if (pageJustRead && !_.includes(newPagesRead, pageJustRead)) {
		const indexToInsert = _.sortedIndex(newPagesRead, pageJustRead);
		newPagesRead.splice(indexToInsert, 0, pageJustRead);
		chapterModel = setIn(chapterModel, "pagesRead", newPagesRead);
	}

	chapterModel = setIn(
		chapterModel,
		"isCompletelyRead",
		isChapterCompletelyRead(chapterID, newPagesRead, demo)
	);
	return chapterModel;
};

const updateSectionsMarkedDone = function(
	clock,
	chapterModel,
	sectionID,
	demo
) {
	const sectionsMarkedDone = chapterModel.sectionsMarkedDone || [];
	if (sectionID !== undefined && !sectionsMarkedDone.includes(sectionID)) {
		sectionsMarkedDone.push(sectionID);
	}

	chapterModel = setIn(chapterModel, "sectionsMarkedDone", sectionsMarkedDone);

	return chapterModel;
};

const isChapterCompletelyRead = function(chapterID, pagesRead, demo) {
	const chapterPages = ChapterSection.getChapterPages(chapterID, demo);
	// Discount first 2 pages in chapter because they are intro pages
	const contentPages = _.range(chapterPages[0] + 2, chapterPages[1] + 1);
	const contentPagesRead = _.without(
		pagesRead,
		chapterPages[0],
		chapterPages[0] + 1
	);
	return _.xor(contentPages, contentPagesRead).length === 0;
};

/**
 * Determines percentage of pages in bookSectionID that are read/completed
 *
 * @param {number} bookSectionID - sectionID of the book
 * @param {Object} libraryChapter - user library data for that chapter where the section lives. Data from atLibraryChapter(chapterID).
 *
 * @return {number} - percentage (between 0 and 1) of pages read in the section
 */
const percentLibrarySectionRead = (bookSectionID, libraryChapter) => {
	const isCompletelyRead = _.get(
		libraryChapter,
		"model.isCompletelyRead",
		false
	);
	const pagesRead = _.get(libraryChapter, "model.pagesRead", []);
	const sectionsMarkedDone =
		_.get(libraryChapter, "model.sectionsMarkedDone", []) || [];

	let percentage = isCompletelyRead ? 1 : 0;
	if (!isCompletelyRead && _.isInteger(bookSectionID)) {
		const sectionPages = ChapterSection.getChapterSectionPages(bookSectionID);
		const pages = _.range(sectionPages[0], sectionPages[1] + 1);
		percentage = _.intersection(pages, pagesRead).length / pages.length;
		if (sectionsMarkedDone.includes(bookSectionID)) {
			percentage = 1;
		}
	}
	return percentage;
};

const librarySectionCompletelyRead = (bookSectionID, libraryChapter) =>
	percentLibrarySectionRead(bookSectionID, libraryChapter) === 1;

const getSectionIDFromLastVisited = function(chapterID, state) {
	const lastVisited = getLastVisitedPageInChapter(chapterID, state);
	return ChapterSection.getSectionIDFromChapterPage(
		chapterID,
		lastVisited,
		state.page.demo
	);
};

//TODO: retry should be shared with OpsResult retry

const getUpdateRetryDelay = function(chapterModel) {
	if (!chapterModel.updateFails) {
		return null;
	}
	return 5000;
};

const doDelayedRetry = function(chapterModel, dispatchAfter) {
	const delay = getUpdateRetryDelay(chapterModel);
	if (delay) {
		const currentFails = chapterModel.updateFails;
		dispatchAfter(
			{
				type: AT.RETRY_UPDATE_LIBRARY_CHAPTER,
				currentFails,
				chapterModel,
			},
			delay + 100
		);
	}
	return chapterModel;
};

const canRetryUpdate = function(chapterModel, clock, currentFails) {
	const delay = getUpdateRetryDelay(chapterModel);
	if (delay === null || currentFails !== chapterModel.updateFails) {
		return false;
	} else {
		return clock.now() >= chapterModel.lastFailTime + delay;
	}
};

module.exports = {
	setupThrottlerInVault,
	initChapter,
	updateChapterData,
	isValidLibraryGrade,
	getLastVisitedPageInChapter,
	getLastVisitedPageFromNumbers,
	fillLibraryPage,
	updateReadPages,
	updateSectionsMarkedDone,
	percentLibrarySectionRead,
	librarySectionCompletelyRead,
	getSectionIDFromLastVisited,
	doDelayedRetry,
	canRetryUpdate,
};
